import type { Node, NodePositionChange, XYPosition } from '@xyflow/react';

export interface HelperLineResult {
  horizontal?: number;
  vertical?: number;
  snapPosition: Partial<XYPosition>;
}

const nodeWidth = (n: Node) => (typeof n.width === 'number' ? n.width : 180);
const nodeHeight = (n: Node) => (typeof n.height === 'number' ? n.height : 80);

/**
 * Calcula guias de alinhamento (helper lines) quando o nó arrastado se aproxima
 * de bordas/centros de outros nós. Baseado no exemplo oficial do React Flow.
 * Retorna a posição "grudada" (snap) e as coordenadas das linhas a desenhar.
 */
export function getHelperLines(
  change: NodePositionChange,
  nodes: Node[],
  distance = 6,
): HelperLineResult {
  const result: HelperLineResult = { snapPosition: { x: undefined, y: undefined } };
  if (change.type !== 'position' || !change.position) return result;

  const active = nodes.find((n) => n.id === change.id);
  if (!active) return result;

  const w = nodeWidth(active);
  const h = nodeHeight(active);

  const aLeft = change.position.x;
  const aRight = change.position.x + w;
  const aCenterX = change.position.x + w / 2;
  const aTop = change.position.y;
  const aBottom = change.position.y + h;
  const aCenterY = change.position.y + h / 2;

  let vDist = distance;
  let hDist = distance;

  for (const other of nodes) {
    if (other.id === active.id) continue;
    const ow = nodeWidth(other);
    const oh = nodeHeight(other);
    const oLeft = other.position.x;
    const oRight = other.position.x + ow;
    const oCenterX = other.position.x + ow / 2;
    const oTop = other.position.y;
    const oBottom = other.position.y + oh;
    const oCenterY = other.position.y + oh / 2;

    // ---- Vertical (eixo X) ----
    // esquerda ↔ esquerda
    if (Math.abs(aLeft - oLeft) < vDist) {
      result.snapPosition.x = oLeft;
      result.vertical = oLeft;
      vDist = Math.abs(aLeft - oLeft);
    }
    // direita ↔ direita
    if (Math.abs(aRight - oRight) < vDist) {
      result.snapPosition.x = oRight - w;
      result.vertical = oRight;
      vDist = Math.abs(aRight - oRight);
    }
    // esquerda ↔ direita
    if (Math.abs(aLeft - oRight) < vDist) {
      result.snapPosition.x = oRight;
      result.vertical = oRight;
      vDist = Math.abs(aLeft - oRight);
    }
    // direita ↔ esquerda
    if (Math.abs(aRight - oLeft) < vDist) {
      result.snapPosition.x = oLeft - w;
      result.vertical = oLeft;
      vDist = Math.abs(aRight - oLeft);
    }
    // centro ↔ centro
    if (Math.abs(aCenterX - oCenterX) < vDist) {
      result.snapPosition.x = oCenterX - w / 2;
      result.vertical = oCenterX;
      vDist = Math.abs(aCenterX - oCenterX);
    }

    // ---- Horizontal (eixo Y) ----
    // topo ↔ topo
    if (Math.abs(aTop - oTop) < hDist) {
      result.snapPosition.y = oTop;
      result.horizontal = oTop;
      hDist = Math.abs(aTop - oTop);
    }
    // base ↔ base
    if (Math.abs(aBottom - oBottom) < hDist) {
      result.snapPosition.y = oBottom - h;
      result.horizontal = oBottom;
      hDist = Math.abs(aBottom - oBottom);
    }
    // topo ↔ base
    if (Math.abs(aTop - oBottom) < hDist) {
      result.snapPosition.y = oBottom;
      result.horizontal = oBottom;
      hDist = Math.abs(aTop - oBottom);
    }
    // base ↔ topo
    if (Math.abs(aBottom - oTop) < hDist) {
      result.snapPosition.y = oTop - h;
      result.horizontal = oTop;
      hDist = Math.abs(aBottom - oTop);
    }
    // centro ↔ centro
    if (Math.abs(aCenterY - oCenterY) < hDist) {
      result.snapPosition.y = oCenterY - h / 2;
      result.horizontal = oCenterY;
      hDist = Math.abs(aCenterY - oCenterY);
    }
  }

  return result;
}
